<script setup lang="ts">
import type { BadgeVariants } from '~ds/components/ui/badge';

import { useElementSize } from '@vueuse/core';
import { Badge, BadgeDot } from '~ds/components/ui/badge';
import KUICoreCarousel from '~kui/components/KUI/Core/Carousel/KUICoreCarousel.vue';
import { motion, useScroll, useTransform } from 'motion-v';

export interface ImageScatterScrollerItem {
  label?: string;
  image?: string;
}

interface Props {
  items: ImageScatterScrollerItem[];
  heading?: string;
  scrollerContainerClass?: string;
}

const props = defineProps<Props>();

const itemLayoutConfig: { width: number; height: number; imgOffsetX: number; imgOffsetY: number; textX: string; textY: string; textColor: BadgeVariants['tone']; zIndex: 1 | 2 | 3 }[] = [
  // if more than 11 items supplied, it'll loop around and reuse same positioning
  // pixel sizes from figma design, converted to rems
  { width: 190 / 16, height: 279 / 16, imgOffsetX: 1, imgOffsetY: 0, textX: '10%', textY: '10%', textColor: 'lime', zIndex: 2 },
  { width: 222 / 16, height: 278 / 16, imgOffsetX: 18 / 16, imgOffsetY: 200 / 16, textX: '50%', textY: '60%', textColor: 'pink', zIndex: 2 },
  { width: 155 / 16, height: 137 / 16, imgOffsetX: -(222 - 145) / 16, imgOffsetY: (279 + 154) / 16, textX: '10%', textY: '10%', textColor: 'purple', zIndex: 2 },
  { width: 182 / 16, height: 192 / 16, imgOffsetX: -(155 - 55) / 16, imgOffsetY: 37 / 16, textX: '10%', textY: '10%', textColor: 'blue', zIndex: 1 },
  { width: 200 / 16, height: 118 / 16, imgOffsetX: 21 / 16, imgOffsetY: (279 + 131) / 16, textX: '10%', textY: '10%', textColor: 'orange', zIndex: 2 },
  { width: 277 / 16, height: 295 / 16, imgOffsetX: -(200 - 59) / 16, imgOffsetY: 122 / 16, textX: '70%', textY: '10%', textColor: 'lime', zIndex: 1 },
  { width: 229 / 16, height: 218 / 16, imgOffsetX: -(277 - 233) / 16, imgOffsetY: (279 + 42) / 16, textX: '80%', textY: '60%', textColor: 'blue', zIndex: 3 },
  { width: 181 / 16, height: 207 / 16, imgOffsetX: 29 / 16, imgOffsetY: 153 / 16, textX: '10%', textY: '10%', textColor: 'pink', zIndex: 2 },
  { width: 219 / 16, height: 165 / 16, imgOffsetX: -(181 - 141) / 16, imgOffsetY: (279 + 126) / 16, textX: '70%', textY: '70%', textColor: 'coral', zIndex: 2 },
  { width: 238 / 16, height: 147 / 16, imgOffsetX: -(165 - 86) / 16, imgOffsetY: 218 / 16, textX: '80%', textY: '20%', textColor: 'orange', zIndex: 2 },
  { width: 194 / 16, height: 129 / 16, imgOffsetX: 13 / 16, imgOffsetY: (279 + 106) / 16, textX: '10%', textY: '10%', textColor: 'blue', zIndex: 2 },
];

const getItemConfig = (index: number) =>
  itemLayoutConfig[index % itemLayoutConfig.length]!;

const slideClasses = cva('!flex-[0_0_auto]', {
  variants: {
    zIndex: { 1: 'z-1', 2: 'z-2', 3: 'z-3' },
  },
});

const totalContainerHeightRems = computed(() =>
  props.items.reduce((sum, _, i) => sum > getItemConfig(i).height + getItemConfig(i).imgOffsetY ? sum : getItemConfig(i).height + getItemConfig(i).imgOffsetY, 0),
);

const totalContentWidthRems = computed(() =>
  props.items.reduce((sum, _, i) => sum + getItemConfig(i).width + getItemConfig(i).imgOffsetX, 0),
);

const containerRef = ref<HTMLElement>();
const { width: containerWidth } = useElementSize(containerRef);

const fitsInViewport = computed(() =>
  containerWidth.value > 0 && containerWidth.value / 16 >= totalContentWidthRems.value,
);

const imageMotionVariants = {
  odd: {
    offscreen: { x: 100 },
    onscreen: { x: 0, rotate: 0, transition: { duration: 1.8 } },
  },
  even: {
    offscreen: { x: 50 },
    onscreen: { x: 0, rotate: 0, transition: { duration: 2.8 } },
  },
};

const getLabelMotion = (idx: number) =>
  imageMotionVariants[idx % 2 ? 'even' : 'odd']

/**
 * Parallax images
 */

// motion warns unless the scroll container (the document) is positioned
useHead({ htmlAttrs: { class: 'relative' } });

// parallax: progress from when section enters bottom of viewport until it leaves the top
const { scrollYProgress } = useScroll({
  target: containerRef,
  offset: ['start end', 'end start'],
});

const imgParallaxScale = useTransform(scrollYProgress, [0, 1], ['100%', '130%']);
// scroll down → image translates up (negative y); scroll up → translates down
const imgParallaxY = useTransform(scrollYProgress, [0, 1], ['15%', '-15%']);
</script>

<template>
  <div class="relative">
    <div class="px-4 w-full max-w-7xl mx-auto flex flex-wrap gap-8 justify-between pb-9">
      <h2
        v-if="heading"
        class="text-[clamp(3rem,8cqw,5.5rem)] leading-none tracking-tight font-semibold"
      >
        {{ heading }}
      </h2>
      <slot name="header" />
    </div>

    <div
      ref="containerRef"
      class="relative"
      :class="scrollerContainerClass"
    >
      <component
        :is="fitsInViewport ? 'div' : KUICoreCarousel"
        v-bind="fitsInViewport
          ? { class: 'flex justify-center' }
          : {
            loop: true,
            dragFree: true,
            showArrows: false,
            showDots: false,
            align: 'start',
            autoScroll: {
              speed: 0.5,
              startDelay: 0,
              stopOnInteraction: false,
            },
            slideClassName: items.map((_, i) => slideClasses({ zIndex: getItemConfig(i).zIndex })),
            viewportClassName: 'cursor-grab active:cursor-grabbing',
            className: 'z-1',
          }"
      >
        <div
          v-for="(item, i) in items"
          :key="i"
          class="relative"
          :style="{
            width: `${getItemConfig(i).width}rem`,
            height: `${totalContainerHeightRems}rem`,
            marginLeft: `${i === 0 ? Math.max(0, getItemConfig(i).imgOffsetX) : getItemConfig(i).imgOffsetX}rem`,
            zIndex: `${getItemConfig(i).zIndex}`,
          }"
        >
          <div
            class="absolute select-none"
            :style="{
              top: `${getItemConfig(i).imgOffsetY}rem`,
              width: `${getItemConfig(i).width}rem`,
              height: `${getItemConfig(i).height}rem`,
            }"
          >
            <motion.div
              v-if="item.image"
              :variants="imageMotionVariants[i % 2 ? 'even' : 'odd']"
              initial="offscreen"
              while-in-view="onscreen"
              class="overflow-hidden rounded-3xl h-full w-full"
            >
              <motion.div
                :style="{ scale: imgParallaxScale, y: imgParallaxY }"
                class="w-full h-full"
              >
                <NuxtImg
                  :src="item.image"
                  alt=""
                  class="w-full h-full object-cover"
                />
              </motion.div>
            </motion.div>

            <motion.div
              v-if="item.label"
              class="absolute top-0 left-0 uppercase w-max max-w-52"
              :style="{ left: getItemConfig(i).textX, top: getItemConfig(i).textY }"
              :variants="getLabelMotion(i)"
              initial="offscreen"
              while-in-view="onscreen"
            >
              <Badge :tone="getItemConfig(i).textColor">
                <BadgeDot />
                {{ item.label }}
              </Badge>
            </motion.div>
          </div>
        </div>
      </component>

      <!-- dashed line bkg -->
      <motion.svg
        width="8500"
        height="4"
        viewBox="0 0 8500 4"
        class="absolute top-1/2 -mt-0.5 pointer-events-none"
        :initial="{ right: '100%' }"
        :while-in-view="{ right: '0%' }"
        :transition="{ duration: 1 }"
        :in-view-options="{ once: true }"
        aria-hidden="true"
        tabindex="-1"
      >
        <path
          d="M 0 2 H 8500"
          fill="none"
          stroke-width="2.2"
          stroke-dasharray="0 7.2"
          stroke-linecap="round"
          class="stroke-muted-foreground"
        />
      </motion.svg>
    </div>
  </div>
</template>
